import React from "react";
import { EMPTY, formatDate, formatTime } from "../lib/dateTime";
import { formatMoneyLedger, formatMoneyLedgerGroups } from "../lib/formatMoney";

/**
 * Cells shared by the two super-admin ledgers (Transactions, Client Transactions), so the
 * same fact reads the same way on both.
 */

/**
 * The practice a row belongs to. `name` is the server's `_tenant_name`, which falls back to
 * the tenant id for an unnamed or purged practice: then the id is all there is, shown once
 * and as an id (mono), instead of the same string twice over a "name" it never was.
 */
export function PracticeCell({ name, id }) {
  if (!name && !id) return "—";
  if (!name || name === id) return <div className="font-mono text-xs text-slate-500">{id || name}</div>;
  return (
    <>
      <div className="font-medium">{name}</div>
      {id && <div className="font-mono text-[10px] text-slate-400">{id}</div>}
    </>
  );
}

/**
 * A row's timestamp on two short lines (date, then hours and minutes), so the Date column
 * is the width of a date instead of a full locale string with seconds.
 */
export function DateTimeCell({ value, lang }) {
  const date = formatDate(value, lang);
  if (date === EMPTY) return EMPTY;
  return (
    <>
      <div className="whitespace-nowrap">{date}</div>
      <div className="whitespace-nowrap text-xs text-slate-400">{formatTime(value, lang)}</div>
    </>
  );
}

/**
 * A ledger amount ("1 557,50 CAD") as two runs joined by a breakable space: a narrow column
 * wraps BETWEEN the number and its currency code, so the column needs only the width of the
 * figure. `negative` prints a leading minus (a refund).
 *
 * Without `figureCap` the number is one no-wrap run, as wide as the amount. With it the number
 * is a `LedgerFigure` capped at that width (a literal Tailwind `max-w-[...]` class), for a table
 * whose fit must not depend on how large an amount gets.
 */
export function LedgerAmount({ cents, currency, lang, negative = false, figureCap = null }) {
  const code = String(currency || "").trim().toUpperCase();
  return (
    <span className="tabular-nums">
      {figureCap
        ? <LedgerFigure cents={cents} lang={lang} negative={negative} cap={figureCap} />
        : <span className="whitespace-nowrap">{negative ? "−" : ""}{formatMoneyLedger(cents, null, lang)}</span>}
      {code && <>{" "}<span className="whitespace-nowrap">{code}</span></>}
    </span>
  );
}

/**
 * A ledger number (no currency) that never makes its column wider than `cap`, whatever the
 * amount. An unbreakable figure is as wide as the amount, so one large enough total pushes any
 * table past its box. Here each digit group is a no-wrap run ("3 ", "516 ", "760 ", "389,25"),
 * with a break opportunity (`<wbr>`) between two groups and nowhere else, inside an inline box
 * of `width: max-content` capped by `cap`:
 *
 * - a figure narrower than the cap renders, and sizes its column, exactly as one no-wrap run;
 * - a wider one takes the cap's width and breaks between its groups, never inside one.
 *
 * Both the box's narrowest and widest widths are min(figure, cap), so the table's layout cannot
 * squeeze it into breaking while it fits under the cap: a break is a property of the amount and
 * the cap, never of how the table shares out spare width. `cap` is a literal `max-w-[...]` class
 * in `ch`, so it scales with the font that draws the digits. The box resets `white-space`, as a
 * cell or wrapper that is no-wrap would otherwise suppress the break opportunities.
 */
export function LedgerFigure({ cents, lang, negative = false, cap }) {
  const groups = formatMoneyLedgerGroups(cents, lang);
  return (
    <span data-ledger-figure="" className={`inline-block w-max ${cap} whitespace-normal`}>
      {groups.map((group, i) => (
        <React.Fragment key={i}>
          {i > 0 && <wbr />}
          <span className="whitespace-nowrap">{i === 0 && negative ? "−" : ""}{group}</span>
        </React.Fragment>
      ))}
    </span>
  );
}
